import {
  LayoutDashboard, List, User, Users, BookOpen, CheckSquare,
  FileText, Edit, Grid, Send, Calendar, Settings, Clock,
  Banknote, Tag, AlertCircle, ArrowDown, RefreshCw, Shield,
  Wallet, Layers, ClipboardList, Bell, BarChart3, Award,
  Palette, GraduationCap, Percent, Receipt, DollarSign,
} from 'lucide-react';

export interface NavChild {
  to: string;
  label: string;
  badge?: number;
  badgeTone?: 'green' | 'orange' | 'red';
}

export interface NavGroup {
  key: string;
  section: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavChild[];
}

export const navGroups: NavGroup[] = [
  {
    key: 'overview',
    section: 'Overview',
    icon: LayoutDashboard,
    items: [
      { to: 'dashboard', label: 'Dashboard' },
    ],
  },
  {
    key: 'school',
    section: 'School',
    icon: Users,
    items: [
      { to: 'admissions', label: 'Admissions' },
      { to: 'students', label: 'Students' },
      { to: 'families', label: 'Families' },
      { to: 'sessions', label: 'Sessions & Terms' },
      { to: 'classes', label: 'Classes & Arms' },
      { to: 'subjects', label: 'Subjects' },
      { to: 'timetable', label: 'Timetable' },
    ],
  },
  {
    key: 'academics',
    section: 'Academics',
    icon: BookOpen,
    items: [
      { to: 'academics', label: 'Overview' },
      { to: 'subject-registration', label: 'Subject Registration' },
      { to: 'composite-subjects', label: 'Composite Subjects' },
      { to: 'attendance', label: 'Attendance' },
      { to: 'promotions', label: 'Promotions' },
    ],
  },
  {
    key: 'results',
    section: 'Assessments & Results',
    icon: FileText,
    items: [
      { to: 'grading', label: 'Grade Components' },
      { to: 'score-entry', label: 'Score Entry' },
      { to: 'import-scores', label: 'Import Scores' },
      { to: 'results', label: 'Results' },
      { to: 'broadsheet', label: 'Broadsheet' },
      { to: 'class-results', label: 'Class Results' },
      { to: 'remarks', label: 'Remarks' },
      { to: 'publish', label: 'Publish' },
      { to: 'held-results', label: 'Held Results' },
      { to: 'score-locks', label: 'Score Locks' },
      { to: 'alumni', label: 'Alumni & Documents' },
      { to: 'fees-transcripts', label: 'Fees & Transcripts' },
      { to: 'sheet-designer', label: 'Sheet Designer' },
      { to: 'signatories', label: 'Signatories' },
    ],
  },
  {
    key: 'teaching',
    section: 'Teaching tools',
    icon: GraduationCap,
    items: [
      { to: 'lessons', label: 'Lessons' },
      { to: 'live-classes', label: 'Live Classes' },
      { to: 'lesson-notes', label: 'Lesson Notes Review' },
      { to: 'question-banks', label: 'Question Banks' },
      { to: 'cbt-exams', label: 'CBT Exams' },
    ],
  },
  {
    key: 'finance',
    section: 'Finance',
    icon: Banknote,
    items: [
      { to: 'approvals', label: 'Approvals', badgeTone: 'orange' },
      { to: 'fees', label: 'Fees & Invoices' },
      { to: 'discounts', label: 'Discounts' },
      { to: 'record-payment', label: 'Collect a Payment' },
      { to: 'payments', label: 'Payments & Receipts' },
      { to: 'debtors', label: 'Debtors & Reminders' },
      { to: 'reconciliation', label: 'Reconciliation' },
      { to: 'wallet', label: 'Wallet' },
      { to: 'withdrawals', label: 'Withdrawals' },
      { to: 'expenses', label: 'Expenses' },
      { to: 'payroll', label: 'Payroll' },
      { to: 'stock', label: 'Store & Stock' },
      { to: 'books', label: 'Books (Ledger)' },
      { to: 'billing', label: 'OJP Billing & Auto-renew' },
    ],
  },
  {
    key: 'people',
    section: 'People & Access',
    icon: User,
    items: [
      { to: 'staff', label: 'Staff' },
      { to: 'roles', label: 'Roles & Permissions' },
      { to: 'logins', label: 'Logins & Passwords' },
      { to: 'desk-access', label: 'Desk Officer Access' },
      { to: 'bursar-access', label: 'Bursar Access & Payment Rules' },
      { to: 'teacher-access', label: 'Teacher Access' },
      { to: 'parent-access', label: 'Parent Access' },
      { to: 'student-access', label: 'Student Access' },
      { to: 'applicant-access', label: 'Applicant Access' },
      { to: 'clock-in', label: 'Staff Attendance & QR' },
    ],
  },
  {
    key: 'communication',
    section: 'Communication',
    icon: Bell,
    items: [
      { to: 'messages', label: 'Messages' },
      { to: 'notices', label: 'Notices & SMS' },
      { to: 'calendar', label: 'Calendar' },
      { to: 'birthdays', label: 'Birthdays' },
      { to: 'support', label: 'OJP Support' },
    ],
  },
  {
    key: 'reports',
    section: 'Reports & Settings',
    icon: BarChart3,
    items: [
      { to: 'reports', label: 'Reports' },
      { to: 'settings', label: 'School Settings' },
      { to: 'account', label: 'My account' },
      { to: 'branding', label: 'Branding' },
      { to: 'audit-log', label: 'Audit Log' },
    ],
  },
];

/** Flat lookup — used for breadcrumb & active-group auto-expand */
export function flattenNav(): NavChild[] {
  return navGroups.flatMap((g) => g.items);
}

/** Which group contains this route? Used to auto-expand on load. */
export function groupForRoute(to: string): string | null {
  for (const g of navGroups) {
    if (g.items.some((i) => i.to === to)) return g.key;
  }
  return null;
}
